import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { courseAdi12StudentDashboard } from 'dsoapi';
import {
  Container,
  Header,
  Segment,
  Divider,
  Grid,
  Label,
  Icon,
} from 'semantic-ui-react';

const Adi12StudentDashboard = ({ setTitle = () => {} }) => {
  const { uceId } = useParams();
  const [dashboardData, setDashboardData] = useState([]);
  const [loading, setLoading] = useState(true);

  setTitle('ADI12 Dashboard');

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const response = await courseAdi12StudentDashboard(uceId);
        setDashboardData(response.data || []);
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [uceId]);

  // Group data by topic and position
  const groupedData = dashboardData.reduce((acc, item) => {
    const position = item.position || 'Unknown';
    const topic = item.topic || 0;
    const topicName = item.coursePageName || `Scenario Topic ${topic}`;

    if (!acc[position]) {
      acc[position] = {};
    }

    if (!acc[position][topicName]) {
      acc[position][topicName] = [];
    }

    acc[position][topicName].push(item);
    return acc;
  }, {});

  const renderScenario = (scenario) => (
    <Segment raised key={scenario.userScenarioId}>
      <Grid>
        <Grid.Row>
          <Grid.Column width={16}>
            <Header as="h4">{scenario.scenarioText}</Header>
          </Grid.Column>
        </Grid.Row>

        <Grid.Row>
          <Grid.Column width={16}>
            <Header as="h5" color="blue">
              Student's Answer
            </Header>
            <p style={{ whiteSpace: 'pre-wrap' }}>{scenario.usersAnswer}</p>
          </Grid.Column>
        </Grid.Row>

        <Grid.Row>
          <Grid.Column width={16}>
            {scenario.scoreGiven > 0 && (
              <Label
                color={
                  scenario.scoreGiven >= 90
                    ? 'green'
                    : scenario.scoreGiven >= 70
                    ? 'yellow'
                    : 'red'
                }
                size="large"
              >
                <Icon name="star" /> Score: {scenario.scoreGiven}
              </Label>
            )}
          </Grid.Column>
        </Grid.Row>

        {scenario.instructorFeedback && (
          <Grid.Row>
            <Grid.Column width={16}>
              <Header as="h5" color="teal">
                Instructor Feedback
              </Header>
              <p style={{ whiteSpace: 'pre-wrap' }}>
                {scenario.instructorFeedback}
              </p>
            </Grid.Column>
          </Grid.Row>
        )}
      </Grid>
    </Segment>
  );

  const renderTopicSection = (position, topicName, scenarios) => {
    // Filter scenarios to only include those with populated answers
    const filteredScenarios = scenarios.filter(
      (scenario) => scenario.usersAnswer && scenario.usersAnswer.trim() !== ''
    );

    // Only render the topic section if there are scenarios with answers
    if (filteredScenarios.length === 0) {
      return null;
    }

    return (
      <div key={`${position}-${topicName}`}>
        <Header as="h3" attached="top" block>
          {topicName}
        </Header>
        <Segment attached="bottom">
          {filteredScenarios.map((scenario) => renderScenario(scenario))}
        </Segment>
      </div>
    );
  };

  const renderPositionSection = (position, topics) => {
    // Check if any scenarios have answers
    const hasCompletedScenarios = Object.values(topics).some((scenarios) =>
      scenarios.some(
        (scenario) => scenario.usersAnswer && scenario.usersAnswer.trim() !== ''
      )
    );

    return (
      <div key={position}>
        <Header as="h2" color="blue" dividing>
          {position === 'FirstHalf'
            ? 'First Half'
            : position === 'SecondHalf'
            ? 'Second Half'
            : position}
        </Header>
        {hasCompletedScenarios ? (
          Object.entries(topics).map(([topicName, scenarios]) =>
            renderTopicSection(position, topicName, scenarios)
          )
        ) : (
          <Segment placeholder>
            <Header icon>
              <Icon name="info circle" />
              Scenarios will display here after you complete the{' '}
              {position === 'FirstHalf' ? 'first' : 'second'} half.
            </Header>
          </Segment>
        )}
        <Divider section />
      </div>
    );
  };

  return (
    <Container>
      <Link
        to="/dashboard"
        className="ui primary button"
        style={{ marginBottom: '1em', display: 'inline-block' }}
      >
        <Icon name="arrow left" /> Back to Dashboard
      </Link>
      {loading ? (
        <Segment placeholder loading>
          <Header icon>
            <Icon name="spinner" />
            Loading dashboard data...
          </Header>
        </Segment>
      ) : dashboardData.length === 0 ? (
        <Segment placeholder>
          <Header icon>
            <Icon name="warning sign" />
            No data available for this student
          </Header>
        </Segment>
      ) : (
        Object.entries(groupedData).map(([position, topics]) =>
          renderPositionSection(position, topics)
        )
      )}
    </Container>
  );
};

export default Adi12StudentDashboard;
